import { Component, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { SharedModule } from 'src/app/layouts/full/shared/shared.module';
import { Client } from 'src/app/models/client.model';
import { Clinic } from 'src/app/models/clinic.model';
import { User } from 'src/app/models/user.model';
import { ClientsService } from 'src/app/services/clients.service';
import { ClinicService } from 'src/app/services/clinic.service';
import { ModalCreateClinicService } from 'src/app/services/modal-create-clinic.service';
import { UserService } from 'src/app/services/user.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-clinics',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './clinics.component.html',
  styleUrl: './clinics.component.scss'
})
export class ClinicsComponent {
  client: Client
  updateClientModalVisible: boolean = false
  updateClinicModalVisible: boolean = false
  createClinicFormVisible: boolean = false
  clinics: Clinic[] = []
  filteredClinics: Clinic[] = []
  clientClinics: Clinic[] = []
  selectedClinic: Clinic
  isEditingClinicForm: boolean = false
  isLoading: boolean = false
  mode: string
  selectedIdentifiersList: any;
  user: User
  viewClinicDetails: boolean = false
  showConnectedPlatform: boolean = false
  pageSize = 3;
  displayedColumns: string[] = [
    'name',
    'billing',
    'shipping'
  ];
  currentPage: number = 0

  public viewClinicForm: FormGroup;
  public createClinicForm: FormGroup;

  newClinic: Clinic = {
    ID: '',
    clientID: '',
    name: '',
    connectedPlatform: {
      name: '',
      identifier: '',
      identifiersList: []
    },
    shippingAddress: {
      userId: '',
      contactNameAndSurname: '',
      country: '',
      city: '',
      province: '',
      streetNameAndNumber: '',
      apartmentOrOthers: '',
      postalCode: '',
      phoneNumber: '',
      default: false,
      vat: ''
    },
    billingAddress: {
      userId: '',
      contactNameAndSurname: '',
      country: '',
      city: '',
      province: '',
      streetNameAndNumber: '',
      apartmentOrOthers: '',
      postalCode: '',
      phoneNumber: '',
      default: false,
      vat: ''
    },
    default: false
  };

  constructor(
    private formBuilder: FormBuilder,
    private modalCreateClinicService: ModalCreateClinicService,
    private clinicService: ClinicService,
    private userService: UserService,
    private clientService: ClientsService
  ) {

    this.user = this.userService.user

    this.viewClinicForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      default: [false],
      connectedPlatform: this.formBuilder.group({
        name: ["Edser"],
        identifier: [''],
        identifiersList: ['']
      }),
      shippingAddress: this.formBuilder.group({
        contactNameAndSurname: ['', [Validators.required]],
        country: ['', [Validators.required]],
        city: ['', [Validators.required]],
        province: ['', [Validators.required]],
        streetNameAndNumber: ['', [Validators.required]],
        apartmentOrOthers: ['', [Validators.required]],
        postalCode: ['', [Validators.required]],
        phoneNumber: ['', [Validators.required]]
      }),
      billingAddress: this.formBuilder.group({
        contactNameAndSurname: ['', [Validators.required]],
        country: ['', [Validators.required]],
        city: ['', [Validators.required]],
        province: ['', [Validators.required]],
        streetNameAndNumber: ['', [Validators.required]],
        apartmentOrOthers: ['', [Validators.required]],
        postalCode: ['', [Validators.required]],
        phoneNumber: ['', [Validators.required]]
      })
    });

    this.createClinicForm = this.formBuilder.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      default: [false, { nonNullable: true }],
      connectedPlatform: this.formBuilder.group({
        name: [''],
        identifier: [''],
        identifiersList: ['']
      }),
      shippingAddress: this.formBuilder.group({
        contactNameAndSurname: ['', [Validators.required]],
        country: ['', [Validators.required]],
        city: ['', [Validators.required]],
        province: ['', [Validators.required]],
        streetNameAndNumber: ['', [Validators.required]],
        apartmentOrOthers: ['', [Validators.required]],
        postalCode: ['', [Validators.required]],
        phoneNumber: ['', [Validators.required]]
      }),
      billingAddress: this.formBuilder.group({
        contactNameAndSurname: ['', [Validators.required]],
        country: ['', [Validators.required]],
        city: ['', [Validators.required]],
        province: ['', [Validators.required]],
        streetNameAndNumber: ['', [Validators.required]],
        apartmentOrOthers: ['', [Validators.required]],
        postalCode: ['', [Validators.required]],
        phoneNumber: ['', [Validators.required]]
      })
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.viewClinicForm.patchValue(this.selectedClinic);
    this.createClinicForm.patchValue([''])
    this.isLoading = false;
    this.getClinicsByClientID()
    this.selectedClinic = null
  }

  ngOnInit(): void {
    this.loadClient()
    this.viewClinicForm.patchValue(this.selectedClinic);
    this.createClinicForm.patchValue(['']);
    this.modalCreateClinicService.clinicCreated.subscribe(() => this.getClinicsByClientID())
  }

  loadClient() {
    let getClientDTO = this.userService.user.email

    // Si es role_customer o role_clinic busca el client adecuado (desde el back le añadimos el user la propiedad client)
    if (this.user && this.user.client) {
      getClientDTO = this.user.client.email
    }

    this.clientService.getClient(getClientDTO).subscribe({
      next: (clientFound) => {
        this.client = clientFound;
        this.getClinicsByClientID();

        const distributorParentId = this.client.userID

        // We look for the client parent user to see if it has the edserWebhook
        this.userService.getUserById(distributorParentId).subscribe({
          next: (user) => {
            const distributorParentEdserWebhook = user.edserWebhook

            if (distributorParentEdserWebhook) {
              this.showConnectedPlatform = true
            } else {
              this.showConnectedPlatform = false
            }
          },
          error: (err) => {
            console.error('Error fetching user:', err);
          }
        })
      },
      error: (err) => {
        console.error('Error fetching client:', err);
      }
    });
  }

  getClinicsByClientID() {
    this.isLoading = true
    this.clinics = []
    this.clientClinics = []
    this.clinicService.getClinicsByClientId(this.client.ID).subscribe((data) => {
      // Obtenemos las clínicas y ordenamos la lista para poner en primer lugar la default clinic
      this.clinics = data.sort((a, b) => (b.default ? 1 : -1) - (a.default ? 1 : -1));
      this.filteredClinics = this.clinics
      this.isLoading = false
    })
  }

  updateClinicMultipleIdentifiers() {
    this.selectedClinic.connectedPlatform.identifiersList = this.selectedClinic.connectedPlatform.identifiersList
      .filter((identifier: any) => identifier !== null);

    // Llamada al servicio para actualizar la lista de identificadores
    this.clinicService.updateIdentifiersList(this.selectedClinic).subscribe({
      next(response) {
        Swal.fire('Clinic identifiers updated successfully', '', 'success');
      },
      error(err) {
        Swal.fire('Error updating clinic identifiers', err.message, 'error');
      },
    });
  }

  updateNewClinicMultipleIdentifiers() {
    this.newClinic.connectedPlatform.identifiersList = this.createClinicForm.get('connectedPlatform.identifiersList').value;
  }

  updatedClinic() {
    this.updateClinicInDB();
  }

  createClinicInDB() {
    if (this.createClinicForm.valid) {
      this.createClinicFormVisible = false
      Swal.showLoading();

      const {
        name,
        default: isDefaultClinic,
        connectedPlatform: {
          identifier: connectedPlatformIdentifier
        },
        shippingAddress: {
          contactNameAndSurname: shippingContactName,
          country: shippingCountry,
          city: shippingCity,
          streetNameAndNumber: shippingStreet,
          apartmentOrOthers: shippingApartment,
          postalCode: shippingPostalCode,
          province: shippingProvince,
          phoneNumber: shippingPhoneNumber,
        },
        billingAddress: {
          contactNameAndSurname: billingContactName,
          country: billingCountry,
          city: billingCity,
          streetNameAndNumber: billingStreet,
          apartmentOrOthers: billingApartment,
          postalCode: billingPostalCode,
          province: billingProvince,
          phoneNumber: billingPhoneNumber,
        }
      } = this.createClinicForm.value;

      const clientID = this.client.ID

      const clinicData = {
        clientID,
        name,
        default: isDefaultClinic,
        connectedPlatform: {
          name: "Edser",
          identifier: connectedPlatformIdentifier,
          identifiersList: this.newClinic.connectedPlatform.identifiersList
        },
        shippingAddress: {
          contactNameAndSurname: shippingContactName,
          country: shippingCountry,
          city: shippingCity,
          streetNameAndNumber: shippingStreet,
          apartmentOrOthers: shippingApartment,
          postalCode: shippingPostalCode,
          province: shippingProvince,
          phoneNumber: shippingPhoneNumber,
          default: true
        },
        billingAddress: {
          contactNameAndSurname: billingContactName,
          country: billingCountry,
          city: billingCity,
          streetNameAndNumber: billingStreet,
          apartmentOrOthers: billingApartment,
          postalCode: billingPostalCode,
          province: billingProvince,
          phoneNumber: billingPhoneNumber,
          default: true
        }
      };

      this.clinicService.createClinic(clinicData as Clinic).subscribe({
        next: (response) => {
          this.createClinicFormVisible = false
          this.modalCreateClinicService.clinicCreated.emit('clinic_created');
          Swal.fire('Clinic created', '', 'success');
        },
        error: (err) => {
          this.createClinicFormVisible = false;
          Swal.fire('Creation failed', err.message, 'error');
        },
      })
    } else {
      Swal.fire('Form is invalid', '', 'error');
    }

    this.createClinicForm.disable()
    this.createClinicForm.reset()
  }

  updateClinicInDB() {
    if (this.viewClinicForm.valid) {
      Swal.showLoading();

      const {
        name,
        default: isDefaultClinic,
        connectedPlatform: {
          identifier: connectedPlatformIdentifier,
        },
        shippingAddress: {
          contactNameAndSurname: shippingContactName,
          country: shippingCountry,
          city: shippingCity,
          streetNameAndNumber: shippingStreet,
          apartmentOrOthers: shippingApartment,
          postalCode: shippingPostalCode,
          province: shippingProvince,
          phoneNumber: shippingPhoneNumber,
        },
        billingAddress: {
          contactNameAndSurname: billingContactName,
          country: billingCountry,
          city: billingCity,
          streetNameAndNumber: billingStreet,
          apartmentOrOthers: billingApartment,
          postalCode: billingPostalCode,
          province: billingProvince,
          phoneNumber: billingPhoneNumber,
        }
      } = this.viewClinicForm.value;

      const clientID = this.selectedClinic.clientID

      const clinicData = {
        ID: this.selectedClinic.ID,
        clientID,
        default: isDefaultClinic,
        connectedPlatform: {
          name: "Edser",
          identifier: connectedPlatformIdentifier,
          identifiersList: this.selectedClinic.connectedPlatform.identifiersList
        },
        name,
        shippingAddress: {
          contactNameAndSurname: shippingContactName,
          country: shippingCountry,
          city: shippingCity,
          streetNameAndNumber: shippingStreet,
          apartmentOrOthers: shippingApartment,
          postalCode: shippingPostalCode,
          province: shippingProvince,
          phoneNumber: shippingPhoneNumber,
          vat: null,
          default: true
        },
        billingAddress: {
          contactNameAndSurname: billingContactName,
          country: billingCountry,
          city: billingCity,
          streetNameAndNumber: billingStreet,
          apartmentOrOthers: billingApartment,
          postalCode: billingPostalCode,
          province: billingProvince,
          phoneNumber: billingPhoneNumber,
          vat: null,
          default: true
        }
      };

      this.clinicService.updateClinic(this.selectedClinic.ID, clinicData as Clinic).subscribe((response) => {
        this.updateClinicModalVisible = false
        this.modalCreateClinicService.clinicCreated.emit('clinic_updated');
        Swal.fire('Clinic updated', '', 'success');
        this.isEditingClinicForm = false;
      },
        (error) => {
          this.updateClinicModalVisible = false;
          Swal.fire('Update failed', error.error.message, 'error');
        }
      )
    } else {
      Swal.fire('Form is invalid', '', 'error');
    }

    this.viewClinicForm.reset()
    this.closeUpdateClinicModal()
    this.selectedClinic = null
  }

  copyShippingAddress(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.createClinicForm.get('billingAddress').setValue(this.createClinicForm.get('shippingAddress').value);
    } else {
      this.createClinicForm.get('billingAddress').reset();
    }
  }

  get identifiersList() {
    return this.createClinicForm.get('connectedPlatform.identifiersList');
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();

    if (!filterValue) {
      this.filteredClinics = this.clinics; // Si no hay filtro, mostrar todas
      return;
    }

    this.filteredClinics = this.clinics.filter(clinic =>
      clinic.name.toLowerCase().includes(filterValue)
    );
  }

  // Este método controla el cambio de página en el paginator
  onPageChange(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
  }

  openClinicDetails(clinic: Clinic) {
    this.selectedClinic = clinic
    this.viewClinicForm.patchValue(clinic)
    this.viewClinicDetails = true
  }

  closeModal() {
    this.viewClinicForm.reset()
  }

  openUpdateClinicModal() {
    this.viewClinicForm.enable()
    this.isEditingClinicForm = true
  }

  closeUpdateClinicModal() {
    this.isEditingClinicForm = false
  }

  openCreateClinicModal() {
    this.createClinicForm.reset()
    this.createClinicForm.enable()
    this.createClinicFormVisible = true
  }

  closeCreateClinicModal() {
    this.createClinicForm.disable()
    this.createClinicFormVisible = false
  }
}
